import { History } from "history";
import React from "react";

import { isNode } from "@use/is-node";

import { HistoryContext } from "./HistoryContext";

/**
 * Specification of props required by the Redirect component
 */
export type RedirectProps = {
  /**
   * Specifies the path that we're going to redirect the user to as soon as
   * this component is mounted into the DOM.
   */
  to: string;

  /**
   * If this setting is enabled, then the redirect will be pushed as a
   * separate entry in the user's history.
   */
  push?: boolean;

  /**
   * Contains a reference to the History instance that's used by the router
   * that this redirect was wrapped inside.
   */
  history?: History;
};

/**
 * Use this component to configure redirects within the application using basic
 * JSX.
 */
export class RedirectUnwrapped extends React.Component<RedirectProps> {
  /**
   * Redirect the user as soon as the component mounts
   */
  public componentDidMount(): void {
    // Throw an error if attempting to mount with undefined history
    // reference
    if (!this.props.history) {
      throw new Error(
        "<Redirect />: Mounting a redirect outside of a <Router /> context isn't allowed."
      );
    }

    if (this.props.push !== true) {
      this.props.history.replace(this.props.to);
    } else {
      this.props.history.push(this.props.to);
    }
  }

  /**
   * This component should never actually render anything on it's own, so just
   * return null in case it stays in the render tree for some reason.
   */
  public render(): null {
    return null;
  }
}

/**
 * Wrapper around the Redirect, which makes sure that the reference to our
 * history object is available as a prop.
 */
// tslint:disable-next-line variable-name
export const Redirect: React.FC<RedirectProps> = (props: RedirectProps) => (
  <HistoryContext.Consumer>
    {(history) => <RedirectUnwrapped {...props} history={history} />}
  </HistoryContext.Consumer>
);
